import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, FormControl, FormControlLabel, RadioGroup, Stack, Typography } from '@mui/material';
import { Checkbox, Divider, Radio, Tooltip } from '@ghs/components';
import { useUnit } from 'effector-react';
import type { ReactNode } from 'react';
import { $$theme } from 'services/ThemeService/model';
import type { CreateFormData, StidSetupState } from './types';
import StidMailerIdSetup from './StidMailerIdSetup';
import { $$campaignSetup } from './model';

type Props = {
  formData: Partial<CreateFormData>;
  onChange: (patch: Partial<CreateFormData>) => void;
  stidSetup: StidSetupState;
};

// Left column is a fixed width so all 4 rows stay aligned
const LEFT_COL_WIDTH = 280;

function LabeledRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" sx={{ py: 1.5 }}>
      <Box sx={{ minWidth: LEFT_COL_WIDTH, flexShrink: 0 }}>{label}</Box>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Stack>
  );
}

export default function ServicesAndOptionsSection({ formData, onChange, stidSetup }: Props) {
  const { palette } = useUnit($$theme.$palette);

  const requiredSup = (
    <sup style={{ color: palette.error.main, fontSize: '1em' }} aria-label="This is a required field">
      *
    </sup>
  );

  const acsEnabled = formData.acsEnabled ?? false;
  const imbEnabled = formData.createUniqueInboundImb ?? false;

  return (
    <Box>
      <LabeledRow label={<Typography variant="body1">{requiredSup} Class of Mail</Typography>}>
        <FormControl>
          <RadioGroup row value={formData.classOfMail ?? ''} onChange={e => onChange({ classOfMail: e.target.value as CreateFormData['classOfMail'] })} sx={{ gap: 4 }}>
            <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }} value="1st-class" control={<Radio />} label="1st Class" />
            <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }} value="marketing-mail" control={<Radio />} label="Marketing Mail" />
          </RadioGroup>
        </FormControl>
      </LabeledRow>

      <Divider />

      <LabeledRow label={<Typography variant="body1">{requiredSup} Service Type</Typography>}>
        <FormControl>
          <RadioGroup row value={formData.serviceType ?? ''} onChange={e => onChange({ serviceType: e.target.value as CreateFormData['serviceType'] })} sx={{ gap: 4 }}>
            <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }} value="full-service" control={<Radio />} label="Full Service" />
            <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }} value="basic-service" control={<Radio />} label="Basic Service" />
          </RadioGroup>
        </FormControl>
      </LabeledRow>

      <Divider />

      <LabeledRow
        label={
          <FormControlLabel
            sx={{ m: 0, '& .MuiFormControlLabel-label': { ml: -1.5 } }}
            control={
              <Checkbox
                checked={acsEnabled}
                onChange={e =>
                  onChange({ acsEnabled: e.target.checked, acsType: e.target.checked ? formData.acsType : null, secureDestruction: e.target.checked ? formData.secureDestruction : false })
                }
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body1">ACS</Typography>
                <Tooltip title="Automatic Identification Service">
                  <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer' }} />
                </Tooltip>
              </Stack>
            }
          />
        }
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <FormControl>
            <RadioGroup row value={formData.acsType ?? ''} onChange={e => onChange({ acsType: e.target.value as CreateFormData['acsType'] })} sx={{ gap: 4 }}>
              <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }} value="asr" control={<Radio disabled={!acsEnabled} />} label="ASR" disabled={!acsEnabled} />
              <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }} value="csr" control={<Radio disabled={!acsEnabled} />} label="CSR" disabled={!acsEnabled} />
            </RadioGroup>
          </FormControl>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }}
            control={<Checkbox checked={formData.secureDestruction ?? false} onChange={e => onChange({ secureDestruction: e.target.checked })} disabled={!acsEnabled} />}
            label="Secure Destruction"
            disabled={!acsEnabled}
          />
        </Stack>
      </LabeledRow>

      <Divider />

      {/* Create Unique Inbound IMb row */}
      <LabeledRow
        label={
          <FormControlLabel
            sx={{ m: 0, '& .MuiFormControlLabel-label': { ml: -1.5 } }}
            control={<Checkbox checked={imbEnabled} onChange={e => onChange({ createUniqueInboundImb: e.target.checked, inboundImbType: e.target.checked ? formData.inboundImbType : null })} />}
            label="Create Unique Inbound IMb"
          />
        }
      >
        <FormControl>
          <RadioGroup row value={formData.inboundImbType ?? ''} onChange={e => onChange({ inboundImbType: e.target.value as CreateFormData['inboundImbType'] })} sx={{ gap: 4 }}>
            <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }} value="brm" control={<Radio disabled={!imbEnabled} />} label="BRM" disabled={!imbEnabled} />
            <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { ml: -1.5 } }} value="crm" control={<Radio disabled={!imbEnabled} />} label="CRM" disabled={!imbEnabled} />
          </RadioGroup>
        </FormControl>
      </LabeledRow>

      <Divider />

      {/* STID / Mailer ID Setup */}
      <StidMailerIdSetup
        stidSetup={stidSetup}
        onAvailableStidSelected={$$campaignSetup.availableStidSelected}
        onActiveStidAdded={$$campaignSetup.activeStidAdded}
        onActiveStidSelected={$$campaignSetup.activeStidSelected}
        onActiveStidRemoved={$$campaignSetup.activeStidRemoved}
        onActiveMailerIdChanged={$$campaignSetup.activeMailerIdChanged}
        onStidPairSaved={$$campaignSetup.stidPairSaved}
        onStidPairRemoved={$$campaignSetup.stidPairRemoved}
      />

      <Divider sx={{ mt: 2 }} />

      {/* Name/Address Required */}
      <Box sx={{ py: 1.5 }}>
        <FormControlLabel
          sx={{ ml: 0, '& .MuiFormControlLabel-label': { ml: -1.5 } }}
          control={<Checkbox checked={formData.nameAddressRequired ?? false} onChange={e => onChange({ nameAddressRequired: e.target.checked })} />}
          label="Name/Address Required"
        />
      </Box>
    </Box>
  );
}
